import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Flex, Tooltip, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import { DateField } from '@refinedev/antd';
import type { ChargeMonthlyOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { PublicationByProductLink } from '@/components/user_catalog/publications/PublicationByProductLink';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationByProduct } from '@/components/user_catalog/publications/usePublicationByProduct';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { PaymentMomentTag } from '@/components/tenancy/tags';
import { formatDuration } from '@/utils/duration';
import { formatLedger, formatPayable, formatUnitPrice } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';

const T = 'settlement.charges_monthly';

/** Seconds rendered as a duration, with the raw count one hover away. */
const SecondsCell = ({ value }: { value: number }) => (
  <Tooltip title={`${value}s`}>
    <span>{formatDuration(value)}</span>
  </Tooltip>
);

export const ChargeMonthlyGeneralTab = ({
  record,
}: {
  record: ChargeMonthlyOut;
}) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  // Shares its request with the product link in the same table.
  const kind = productKind(
    usePublicationByProduct(record.product_id),
    record.product_id
  );

  const aggregateItems: DescriptionsProps['items'] = [
    {
      key: 'bucket',
      label: t(`${T}.fields.bucket`),
      children: <DateField value={record.bucket} format="YYYY-MM" />,
    },
    {
      key: 'publisher_id',
      label: t(`${T}.fields.publisher_id`),
      children: <PublisherIconLink publisherId={record.publisher_id} />,
    },
    {
      key: 'account_id',
      label: t(`${T}.fields.account_id`),
      children: orDash(record.account_id),
    },
    {
      key: 'profile_id',
      label: t(`${T}.fields.profile_id`),
      children: <ProfileIconLink profileId={record.profile_id} />,
    },
    {
      key: 'product_id',
      label: t(`${T}.fields.product_id`),
      children: (
        <Flex gap={4} align="center" wrap>
          <PublicationByProductLink productId={record.product_id} />
          <Typography.Text type="secondary">
            {`v${record.product_version}`}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      key: 'product_format',
      label: t(`${T}.fields.product_format`),
      children: orDash(kind && <ProductFormatTag kind={kind} />),
    },
    {
      key: 'product_duration',
      label: t(`${T}.fields.product_duration`),
      children: <SecondsCell value={record.product_duration} />,
    },
    {
      key: 'basis_seconds',
      label: t(`${T}.fields.basis_seconds`),
      children: <SecondsCell value={record.basis_seconds} />,
    },
    {
      key: 'charges_count',
      label: t(`${T}.fields.charges_count`),
      children: record.charges_count,
    },
    {
      key: 'unit_price',
      label: (
        <HintLabel
          label={t(`${T}.fields.unit_price`)}
          hint={t('settlement.hints.unit_price')}
        />
      ),
      children: formatUnitPrice(record.unit_price, record.currency),
    },
    {
      key: 'payable_unit_price',
      label: (
        <HintLabel
          label={t(`${T}.fields.payable_unit_price`)}
          hint={t('settlement.hints.payable_unit_price')}
        />
      ),
      children: formatPayable(record.payable_unit_price, record.currency),
    },
    {
      key: 'amount',
      label: (
        <HintLabel
          label={t(`${T}.fields.amount`)}
          hint={t('settlement.hints.amount_ledger')}
        />
      ),
      children: formatLedger(record.amount, record.currency),
    },
    {
      key: 'currency',
      label: t(`${T}.fields.currency`),
      children: orDash(record.currency),
    },
    { key: 'id', label: t('base.fields.id'), children: orDash(record.id) },
  ];

  const rateItems: DescriptionsProps['items'] = [
    {
      key: 'contract_rate_id',
      label: t(`${T}.fields.contract_rate_id`),
      children: record.contract_rate_id,
    },
    {
      key: 'rate_value',
      label: t(`${T}.fields.rate_value`),
      children: formatLedger(record.rate_value, record.currency),
    },
    {
      key: 'rate_kind',
      label: t(`${T}.fields.rate_kind`),
      children: t(`tenancy.contracts.rate_kind.${record.rate_kind}`),
    },
    {
      key: 'rate_variant',
      label: t(`${T}.fields.rate_variant`),
      children: t(`tenancy.contracts.rate_variant.${record.rate_variant}`),
    },
    {
      key: 'payment_moment',
      label: t(`${T}.fields.payment_moment`),
      children: <PaymentMomentTag moment={record.payment_moment} />,
    },
  ];

  return (
    <Flex vertical gap={16}>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, lg: 3 }}
          items={aggregateItems}
        />
      </Card>
      <Card title={t(`${T}.sections.rate`)}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, lg: 3 }}
          items={rateItems}
        />
      </Card>
    </Flex>
  );
};
